import { faCheck, faEdit, faTrashAlt, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type CellContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/utils/messages';

import type { AdditionalServices } from 'src/features/parkingManagement/types/additionalServices';

interface CellAdditionalServiceActionsProps<TData, TValue> extends CellContext<TData, TValue> {
    deleteCallback: (point: TData) => void;
    editCallback: (point: TData) => void;
    deactivateCallback: (point: TData) => void;
    activateCallback: (point: TData) => void;
}

const CellAdditionalServiceActions = ({
    deleteCallback,
    editCallback,
    deactivateCallback,
    activateCallback,
    row,
}: CellAdditionalServiceActionsProps<AdditionalServices, unknown>) => {
    const { formatMessage } = useIntl();
    const permissionAdditionalServices = useHasPermissions([PERMISSION.ADDITIONAL_SERVICES_MANAGE]);
    const serviceIsUsed = row.original.is_used;

    return (
        <div className="flex">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild className="cursor-pointer">
                        <Button
                            onClick={() => editCallback(row.original)}
                            variant="link"
                            className="h-10 px-1.5"
                            disabled={!permissionAdditionalServices || serviceIsUsed}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{formatMessage(generalMessages.edit)}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild className="cursor-pointer">
                        <Button
                            onClick={() => deleteCallback(row.original)}
                            className="h-10 px-1.5"
                            variant="link"
                            disabled={!permissionAdditionalServices || serviceIsUsed}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{formatMessage(generalMessages.delete)}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild className="cursor-pointer">
                        <Button
                            onClick={() => deactivateCallback(row.original)}
                            className="h-10 px-1.5"
                            variant="link"
                            disabled={!permissionAdditionalServices || !row.original.is_enabled}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{formatMessage(generalMessages.deactivate)}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild className="cursor-pointer">
                        <Button
                            onClick={() => activateCallback(row.original)}
                            className="h-10 px-1.5"
                            variant="link"
                            disabled={!permissionAdditionalServices || row.original.is_enabled}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{formatMessage(generalMessages.activate)}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
};

export default CellAdditionalServiceActions;
